import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Paper from '@mui/material/Paper';
import cn from 'classnames';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import Slider, { Settings } from 'react-slick';
import { useTranslation } from 'react-i18next';

import {
	AviaSearchParameters,
	FareFamilyFragment,
	FareFamilyPriceFragment,
	FlightClass,
	InfoMessagePositionsEnum
} from '@websky/graphql';
import { default as BaseButton } from '../../../BaseComponents/Button';
import { useDesktopFlightModalContent, useDesktopFlightModalMobileTabs, useLastSelectedFlight } from './hooks';
import { SelectedFare, Tabs, UseDesktopFlightWithPopup } from '../../types';
import { useTheme } from '../../../theme';
import { useConfig } from '../../../context';
import { SimpleFlight } from '../../../Results/types';
import { FlightInfoV2 } from '../../../FlightInfo';
import { usePagination } from '@shared/ui';
import { MOBILE_MIN_WIDTH, TABLET_MIN_WIDTH } from '../../../utils';
import { PaginationWithOverride, Tabs as MobileTabs } from '@shared/ui';
import CompareFares from '../../../CompareFares/components/CompareFares/CompareFares';
import Link from '../../../Link';
import { useRenderers } from '../../../renderProps';
import {
	findIndexFirstAvailableFareGroup,
	isFareGroupAvailable,
	isFaresHasOneItem,
	isShowFaresPagination,
	roundAvailableSliderIndex
} from './utils';
import FlightInfoSegment from '../FlightInfoSegment/FlightInfoSegment';
import { getDurationString, getFlightDuration } from '../../../Results/utils';
import { getCheapestFlightInCombination, getNextSegment } from '../../utils';
import {
	analytics_fareAddToCart,
	analytics_setFareGroupsListItems
} from '../../../Results/components/Results/dataLayer/results';
import InfoMessages from '../../../InfoMessages';
import { filterMessageByPosition } from '../../../InfoMessages/utils';
import { SwipeableDrawer } from '@mui/material';
import { useToggleable } from '../../../hooks';
import ModalX from '../../../BaseComponents/ModalX';
import SlickSlider from '../../../SlickSlider';
import { CdnIcon } from '@shared/ui';
import { useGetFfpBonusInfoById } from '@features/fares';
import { FareGroup as FareGroupDefault } from '@features/fares';

export type SlidesCountSettings = {
	[key in FlightClass]: Partial<{
		desktop: number;
		tablet: number;
		phone: number;
		phoneSmall: number;
	}>;
};

export interface Props {
	legId?: number;
	flight: SimpleFlight;
	economyFares: FareFamilyPriceFragment[];
	businessFares: FareFamilyPriceFragment[];
	milesFares: FareFamilyPriceFragment[];
	getOnFareSelect: ReturnType<UseDesktopFlightWithPopup>['getOnFareSelect'];
	upgradeOptions: ReturnType<UseDesktopFlightWithPopup>['upgradeOptions'];
	lastSelectedFare?: FareFamilyFragment;
	preselectedFlightClass?: FlightClass;
	activeTab?: Tabs;
	hidePrice?: boolean;
	isReturnFlight?: boolean;
	isSelectorInModal?: boolean;
	searchParameters?: AviaSearchParameters;
	onClose?: () => void;
	sliderSettings?: Settings;
	slidesToShowOnce?: number;
	orderId?: string;
	setIsComparisonOpen?: (isOpen: boolean) => void;
	flightId?: string;
	selectedFares: SelectedFare[];
	isFaresMilesMustBeRequested?: boolean;
	/** Нужно ли делать скролл до кнопки тарифа */
	enableScrollIntoViewFareButton?: boolean;
}

const DesktopFlightModalContent: React.FC<Props> = props => {
	const { isSelectorInModal = true, enableScrollIntoViewFareButton = false } = props;

	const {
		active,
		handleBusinessButtonClick,
		handleEconomyButtonCLick,
		handleMilesButtonClick,
		isFareCompareOpen,
		openFareCompare,
		closeFareCompare
	} = useDesktopFlightModalContent(props);

	const { t } = useTranslation('FlightInfoModalContent');

	const {
		FareGroup = FareGroupDefault,
		DesktopFlightContentFlightInfo: DesktopFlightContentFlightInfoOverride = FlightInfoV2
	} = useRenderers();

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const isSmallMobile = useMediaQuery({ maxWidth: 380 });

	const isSelectorInModalOrMobile = isSelectorInModal || isMobile;

	const {
		Engine: { resultsFareSlidesToShow }
	} = useConfig();

	const allFares = useMemo(() => {
		return !isSelectorInModalOrMobile ? [...props.economyFares, ...props.businessFares, ...props.milesFares] : [];
	}, [isSelectorInModalOrMobile, props.economyFares, props.businessFares, props.milesFares]);

	const lastSelectedFare = useLastSelectedFlight();

	const isActiveEconomyFares = active === Tabs.EconomyFares;
	const isActiveBusinessFares = active === Tabs.BusinessFares;
	const isActiveMilesFares = active === Tabs.MilesFares;
	const isActiveFlightInfo = active === Tabs.FlightInfo;

	const isOneEconomyFare = isFaresHasOneItem(props.economyFares);
	const isOneBusinessFare = isFaresHasOneItem(props.businessFares);
	const isOneMilesFare = isFaresHasOneItem(props.milesFares);
	const isOneAllFare = isFaresHasOneItem(allFares);

	const slidesToShow = React.useMemo(() => {
		if (isSmallMobile) {
			return resultsFareSlidesToShow?.mobile ?? 1;
		}

		if (isMobile) {
			return resultsFareSlidesToShow?.mobile ?? 1.2;
		}

		if (isTablet) {
			return resultsFareSlidesToShow?.tablet ?? 2;
		}

		return props.sliderSettings?.slidesToShow ?? resultsFareSlidesToShow?.desktop ?? 3;
	}, [isSmallMobile, isMobile, isTablet, props.sliderSettings?.slidesToShow, resultsFareSlidesToShow]);

	const slidesToScroll = slidesToShow >= 2 ? slidesToShow : 1;
	const slidesToShowOnce = props.slidesToShowOnce || 1.1;

	const { isOpen, open, close, toggle } = useToggleable(false);

	const { firstEconomyAvailableSliderIndex, firstBusinessAvailableSliderIndex } = useMemo(() => {
		const firstEconomyAvailableFareGroup = findIndexFirstAvailableFareGroup(props.economyFares, lastSelectedFare);
		const firstBusinessAvailableFareGroup = findIndexFirstAvailableFareGroup(props.businessFares, lastSelectedFare);

		const firstEconomyAvailableSliderIndex = roundAvailableSliderIndex(
			firstEconomyAvailableFareGroup,
			slidesToScroll
		);
		const firstBusinessAvailableSliderIndex = roundAvailableSliderIndex(
			firstBusinessAvailableFareGroup,
			slidesToScroll
		);

		return {
			firstEconomyAvailableSliderIndex,
			firstBusinessAvailableSliderIndex
		};
	}, [props.economyFares, props.businessFares, lastSelectedFare, slidesToScroll]);

	const { currentIndex: economySliderIndex, handleChange: handleEconomySliderIndexChange } = usePagination(
		firstEconomyAvailableSliderIndex
	);
	const { currentIndex: businessSliderIndex, handleChange: handleBusinessSliderIndexChange } = usePagination(
		firstBusinessAvailableSliderIndex
	);
	const { currentIndex: milesSliderIndex, handleChange: handleMilesSliderIndexChange } = usePagination(0);
	const { currentIndex: allFaresSliderIndex, handleChange: handleAllFaresSliderIndexChange } = usePagination(0);

	const economySliderRef = React.useRef<Slider>();
	const businessSliderRef = React.useRef<Slider>();
	const milesSliderRef = React.useRef<Slider>();

	useEffect(() => {
		// return to previous item when user change active tab
		handleEconomySliderIndexChange(firstEconomyAvailableSliderIndex);
		handleBusinessSliderIndexChange(firstBusinessAvailableSliderIndex);
	}, [active]);

	useEffect(() => {
		props.setIsComparisonOpen?.(isFareCompareOpen);
	}, [isFareCompareOpen]);

	const { ModalContent: theme } = useTheme('DesktopFlight');

	const { flightIdsToRequestMiles, segmentsForFlightToRequestMiles } = useMemo(() => {
		/** Тарифы по активному табу */
		const faresForActiveTab: FareFamilyPriceFragment[] = (() => {
			switch (active) {
				case Tabs.EconomyFares:
					return props.economyFares;
				case Tabs.BusinessFares:
					return props.businessFares;
				case Tabs.MilesFares:
					return props.milesFares;
				default:
					return [];
			}
		})();

		/** Если есть allFares — используем все тарифы */
		const fares: FareFamilyPriceFragment[] = allFares?.length ? allFares : faresForActiveTab;

		const flightIdsToRequestMiles = fares
			.map(fare => {
				const cheapestFlight = getCheapestFlightInCombination(fare, props.selectedFares);

				return cheapestFlight?.flight?.id;
			})
			.filter(Boolean);

		const segmentsForFlightToRequestMiles = props.flight?.segments?.map(segment => segment?.segment);

		return { flightIdsToRequestMiles, segmentsForFlightToRequestMiles };
	}, [
		active,
		allFares,
		props.flight,
		props.milesFares,
		props.economyFares,
		props.businessFares,
		props.selectedFares
	]);

	const { ffpBonusInfoById } = useGetFfpBonusInfoById({
		legId: props.legId,
		flightIds: flightIdsToRequestMiles,
		segments: segmentsForFlightToRequestMiles,
		needToMakeRequest: props.isFaresMilesMustBeRequested
	});

	const renderFareGroup = (
		group: FareFamilyPriceFragment,
		index: number,
		cheapestFlight: FareFamilyPriceFragment['prices'][0],
		disabledForPriceInMiles: boolean,
		disabled: boolean,
		dataTestId?: string
	) => {
		const onFareSelect = () => {
			analytics_fareAddToCart(group, index, props.flight, props.searchParameters);

			props.getOnFareSelect(group)();
		};

		return (
			<FareGroup
				key={index}
				className={cn(theme.fareGroup__slide, {
					[theme.fareGroup_withoutUpgradeOption]: !!props.lastSelectedFare
				})}
				fare={group.fareFamily}
				price={!props.hidePrice && cheapestFlight?.price ? cheapestFlight.price : null}
				originalPrice={!props.hidePrice && cheapestFlight?.originalPrice ? cheapestFlight.originalPrice : null}
				priceInMiles={cheapestFlight?.priceInMiles}
				ffpBonusInfo={ffpBonusInfoById[cheapestFlight?.flight?.id]}
				upgradeOption={props.upgradeOptions.get(group.fareFamily.id)}
				disabledForMiles={disabledForPriceInMiles}
				dataTestId={dataTestId}
				disabled={disabled}
				onSelect={onFareSelect}
				onUpgrade={
					props.upgradeOptions.has(group.fareFamily.id) &&
					props.getOnFareSelect(props.upgradeOptions.get(group.fareFamily.id).canUpgradeTo)
				}
				showUnavailableOptions
			/>
		);
	};

	const renderFareGroups = (fareGroups: FareFamilyPriceFragment[]) => {
		let availableFareGroupIndex = 0;

		return fareGroups.map((fareGroup, index) => {
			const isAvailable = isFareGroupAvailable(fareGroup, lastSelectedFare);
			const cheapestFlight = getCheapestFlightInCombination(fareGroup, props.selectedFares);
			const disabledForPriceInMiles =
				cheapestFlight?.priceInMiles > 0 && !cheapestFlight?.flight?.canBePaidByMiles;
			const disabled = !isAvailable || disabledForPriceInMiles;
			const dataTestId = disabled ? undefined : (++availableFareGroupIndex).toString();

			return renderFareGroup(fareGroup, index, cheapestFlight, disabledForPriceInMiles, disabled, dataTestId);
		});
	};

	const renderSliderIndex = (slidesCount: number, index: number, slidesToScroll: number) => {
		let pagesCount = Math.ceil(slidesCount / slidesToScroll);

		if (isMobile) {
			pagesCount = slidesCount;
		}

		if ((isTablet && slidesCount > 2) || slidesCount > sliderSettings.slidesToScroll) {
			return (
				<span className={theme.pagination__index}>
					{Math.ceil(index / slidesToScroll) + 1} / {pagesCount}
				</span>
			);
		} else {
			return null;
		}
	};

	const isEconomyExists = React.useMemo(() => {
		return props.economyFares.some(economyFare => economyFare.prices);
	}, [props.economyFares]);

	const isBusinessFaresExists = React.useMemo(() => {
		return props.businessFares.some(businessFare => businessFare.prices);
	}, [props.businessFares]);

	const isMilesExists = React.useMemo(() => {
		return props.milesFares.some(milesFare => milesFare.prices);
	}, [props.milesFares]);

	const showFareClassTabs = [isEconomyExists, isBusinessFaresExists, isMilesExists].filter(Boolean).length > 1;

	const container = useRef<HTMLDivElement>();
	const sliderAnchorRef = useRef<HTMLDivElement>();

	const onScrollTop = useCallback(() => {
		if (!container.current) {
			return;
		}

		container.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
	}, [container]);

	useEffect(() => {
		analytics_setFareGroupsListItems(
			[...props.economyFares, ...props.businessFares, ...props.milesFares],
			props.flight,
			props.searchParameters
		);
	}, []);

	useEffect(() => {
		if (!sliderAnchorRef.current || !enableScrollIntoViewFareButton) {
			return;
		}

		sliderAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [enableScrollIntoViewFareButton]);

	const renderInfoMessages = (position: InfoMessagePositionsEnum, infoMessages: React.ReactNode) => {
		const messages = props.flight.segments[0]?.segment?.messages?.filter(message =>
			filterMessageByPosition(position, message)
		);

		// to prevent the slide from being displayed if there are no messages
		if (messages?.length) {
			return infoMessages;
		}

		return null;
	};

	const infoMessageByClass = React.useMemo(() => {
		const position =
			active === Tabs.EconomyFares
				? InfoMessagePositionsEnum.upsaleEconomy
				: InfoMessagePositionsEnum.upsaleBusiness;

		return renderInfoMessages(
			position,
			<InfoMessages
				position={position}
				className={theme.infoMessage}
				notificationsClassName={theme.infoMessage__notifications}
				itemHtmlClassName={theme.infoMessage__htmlMessage}
				htmlClassName={theme.infoMessage__htmlContainer}
				messages={props.flight.segments[0].segment.messages}
			/>
		);
	}, [props.flight.segments, active]);

	const infoMessageRight = React.useMemo(() => {
		const position = InfoMessagePositionsEnum.upsaleRight;

		return renderInfoMessages(
			position,
			<InfoMessages
				position={position}
				className={cn(theme.infoMessage, theme.upsaleRightInfoMessage)}
				notificationsClassName={theme.infoMessage__notifications}
				itemHtmlClassName={theme.infoMessage__htmlMessage}
				contentClassName={theme.infoMessage__contentClassName}
				messages={props.flight.segments[0].segment.messages}
			/>
		);
	}, [props.flight.segments]);

	const hasInfoMessage = !!infoMessageByClass || !!infoMessageRight;

	const isOneActiveFare =
		(isActiveEconomyFares && isOneEconomyFare && !hasInfoMessage) ||
		(isActiveBusinessFares && isOneBusinessFare && !hasInfoMessage) ||
		(isActiveMilesFares && isOneMilesFare && !hasInfoMessage);

	const sliderSettings: Settings = {
		slidesToShow: isOneActiveFare ? slidesToShowOnce : slidesToShow,
		infinite: false,
		speed: 250,
		slidesToScroll,
		swipe: isTablet,
		...props.sliderSettings,
		className: theme.slider
	};

	/**
	 * У beforeChange в nextSlide может приходить дробное значение.
	 * Округляем, чтобы корректно отображалась пагинация.
	 */
	const handleBeforeChange = (callback: (index: number) => void) => (_: number, nextSlide: number) =>
		callback(Math.round(nextSlide));

	const allSliderSettings = {
		...sliderSettings,
		slidesToShow: isOneAllFare ? slidesToShowOnce : slidesToShow,
		className: cn(sliderSettings.className, { [theme.slider_allOnce]: isOneAllFare }),
		beforeChange: handleBeforeChange(handleAllFaresSliderIndexChange)
	};

	const economySliderSettings = {
		...sliderSettings,
		className: cn(sliderSettings.className, theme.slider_economy),
		initialSlide: firstEconomyAvailableSliderIndex,
		beforeChange: handleBeforeChange(handleEconomySliderIndexChange)
	};

	const businessSliderSettings = {
		...sliderSettings,
		className: cn(sliderSettings.className, theme.slider_business),
		initialSlide: firstBusinessAvailableSliderIndex,
		beforeChange: handleBeforeChange(handleBusinessSliderIndexChange)
	};

	const milesSliderSettings = {
		...sliderSettings,
		className: cn(sliderSettings.className, theme.slider_miles),
		beforeChange: handleBeforeChange(handleMilesSliderIndexChange)
	};

	const sliderWrapperClassName = cn(theme.slider__wrp, {
		[theme.slider__wrp_singleItem]: isOneActiveFare && !hasInfoMessage
	});

	const {
		tabs: mobileFareGroupTabs,
		active: activeFareGroupMobileTab,
		onChange: onChangeFareGroupMobileTabs
	} = useDesktopFlightModalMobileTabs({
		isEconomyExists,
		isMilesExists,
		isBusinessFaresExists,
		showFareClassTabs,
		isActiveEconomyFares,
		isActiveBusinessFares,
		isActiveMilesFares,
		handleEconomyButtonCLick,
		handleBusinessButtonClick,
		handleMilesButtonClick,
		economyFares: props.economyFares,
		businessFares: props.businessFares,
		milesFares: props.milesFares
	});

	return (
		<Paper
			elevation={0}
			data-test-id="fare-modal"
			className={cn(theme.paper, {
				[theme.paper_dropdown]: !isSelectorInModalOrMobile,
				[theme.paper_pagination]:
					!isSelectorInModalOrMobile && allFares.length > allSliderSettings.slidesToScroll
			})}
			ref={container}
		>
			<InfoMessages
				position={InfoMessagePositionsEnum.upsaleTop}
				className={theme.topInfoMessage}
				itemHtmlClassName={theme.topInfoMessage__htmlMessage}
				messages={props.flight.segments[0].segment.messages}
			/>

			<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
				<div className={theme.buttons}>
					{isSelectorInModalOrMobile && showFareClassTabs && (
						<>
							{isEconomyExists && (
								<BaseButton
									variant="optionAction"
									className={cn(theme.fareGroup__button, {
										[theme.fareGroup__button_active]: isActiveEconomyFares
									})}
									data-test-id="fare-modal-tab-economy"
									onClick={handleEconomyButtonCLick}
								>
									{t('Economy')}
								</BaseButton>
							)}

							{isBusinessFaresExists && (
								<BaseButton
									variant="optionAction"
									className={cn(theme.fareGroup__button, theme.fareGroup__button_business, {
										[theme.fareGroup__button_active]: isActiveBusinessFares
									})}
									data-test-id="fare-modal-tab-business"
									onClick={handleBusinessButtonClick}
								>
									{t('Business')}
								</BaseButton>
							)}

							{isMilesExists && (
								<BaseButton
									variant="optionAction"
									className={cn(theme.fareGroup__button, theme.fareGroup__button, {
										[theme.fareGroup__button_active]: isActiveMilesFares
									})}
									data-test-id="fare-modal-tab-miles"
									onClick={handleMilesButtonClick}
								>
									{t('Premium')}
								</BaseButton>
							)}
						</>
					)}

					{active !== Tabs.FlightInfo && (
						<Link className={theme.compareFaresLink} action={openFareCompare}>
							{t('View full conditions')}
							<CdnIcon className={theme.compareFaresLink_icon} icon="Info/Details" />
						</Link>
					)}
				</div>
			</MediaQuery>

			<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
				{mobileFareGroupTabs.length > 0 && (
					<MobileTabs
						classes={{
							tabs: theme.mobileTabs,
							tab: theme.mobileTabs__tab,
							description: theme.mobileTabs__description
						}}
						tabs={mobileFareGroupTabs}
						active={activeFareGroupMobileTab}
						onChange={onChangeFareGroupMobileTabs}
					/>
				)}
			</MediaQuery>

			<div className={theme.content}>
				<div
					className={cn(theme.content_wrapper, {
						[theme.content_offset]: hasInfoMessage
					})}
				>
					<MediaQuery minWidth={MOBILE_MIN_WIDTH}>
						<div>
							{allFares.length > 0 ? (
								<div className={sliderWrapperClassName}>
									<SlickSlider {...allSliderSettings}>{renderFareGroups(allFares)}</SlickSlider>
									{renderSliderIndex(
										allFares.length,
										allFaresSliderIndex,
										allSliderSettings.slidesToScroll
									)}
								</div>
							) : (
								<>
									{isActiveEconomyFares && (
										<div className={sliderWrapperClassName}>
											<SlickSlider {...economySliderSettings}>
												{infoMessageByClass}
												{renderFareGroups(props.economyFares)}
												{infoMessageRight}
											</SlickSlider>
											{renderSliderIndex(
												props.economyFares.length,
												economySliderIndex,
												slidesToScroll
											)}
										</div>
									)}

									{isActiveBusinessFares && (
										<div className={sliderWrapperClassName}>
											<SlickSlider {...businessSliderSettings}>
												{infoMessageByClass}
												{renderFareGroups(props.businessFares)}
												{infoMessageRight}
											</SlickSlider>

											{renderSliderIndex(
												props.businessFares.length,
												businessSliderIndex,
												slidesToScroll
											)}
										</div>
									)}

									{isActiveMilesFares && (
										<div className={sliderWrapperClassName}>
											<SlickSlider {...milesSliderSettings}>
												{renderFareGroups(props.milesFares)}
												{infoMessageRight}
											</SlickSlider>

											{renderSliderIndex(
												props.milesFares.length,
												milesSliderIndex,
												milesSliderSettings.slidesToShow
											)}
										</div>
									)}
								</>
							)}
						</div>

						<div ref={sliderAnchorRef} />

						{isActiveFlightInfo && (
							<DesktopFlightContentFlightInfoOverride
								flight={props.flight}
								selectedFareFamily={props.lastSelectedFare}
							/>
						)}
					</MediaQuery>

					<MediaQuery maxWidth={MOBILE_MIN_WIDTH}>
						{isActiveEconomyFares && (
							<div className={sliderWrapperClassName}>
								<SlickSlider {...economySliderSettings} ref={economySliderRef}>
									{renderFareGroups(props.economyFares)}
								</SlickSlider>

								{isShowFaresPagination(props.economyFares) && (
									<PaginationWithOverride
										className={theme.pagination}
										variant="bigCircles"
										itemsCount={props.economyFares.length}
										currentIndex={economySliderIndex}
										onChange={index => economySliderRef.current.slickGoTo(index)}
									/>
								)}
							</div>
						)}

						{isActiveBusinessFares && (
							<div className={sliderWrapperClassName}>
								<SlickSlider {...businessSliderSettings} ref={businessSliderRef}>
									{renderFareGroups(props.businessFares)}
								</SlickSlider>

								{isShowFaresPagination(props.businessFares) && (
									<PaginationWithOverride
										className={theme.pagination}
										variant="bigCircles"
										itemsCount={props.businessFares.length}
										currentIndex={businessSliderIndex}
										onChange={index => businessSliderRef.current.slickGoTo(index)}
									/>
								)}
							</div>
						)}

						{isActiveMilesFares && (
							<div className={sliderWrapperClassName}>
								<SlickSlider {...milesSliderSettings} ref={milesSliderRef}>
									{renderFareGroups(props.milesFares)}
								</SlickSlider>

								{isShowFaresPagination(props.milesFares) && (
									<PaginationWithOverride
										className={theme.pagination}
										variant="bigCircles"
										itemsCount={props.milesFares.length}
										currentIndex={milesSliderIndex}
										onChange={index => milesSliderRef.current.slickGoTo(index)}
									/>
								)}
							</div>
						)}

						<div ref={sliderAnchorRef} />

						{active !== Tabs.FlightInfo && (
							<div className={theme.compareFaresLink__mobile_wrapper}>
								<Link className={theme.compareFaresLink__mobile} action={openFareCompare}>
									{t('View full conditions')}

									<CdnIcon
										className={theme.compareFaresLink__mobile_icon}
										icon="Arrow/Chevron_right"
									/>
								</Link>
							</div>
						)}

						{hasInfoMessage && (
							<SwipeableDrawer
								anchor="bottom"
								classes={{ paper: theme.swipeable__paper, root: theme.swipeable__paperRoot }}
								open={isOpen}
								onOpen={open}
								onClose={close}
								onClick={toggle}
								swipeAreaWidth={76}
								disableSwipeToOpen={false}
								ModalProps={{ keepMounted: true }}
							>
								{infoMessageRight ?? infoMessageByClass}
							</SwipeableDrawer>
						)}
					</MediaQuery>

					{isSelectorInModalOrMobile && (
						<div className={theme.segments}>
							{props.flight.segments.map((segment, index) => (
								<FlightInfoSegment
									key={index}
									className={theme.segments__flight}
									segment={segment}
									isMobile={isTablet}
									nextSegment={
										segment.transferDuration ? getNextSegment(props.flight.segments, index) : null
									}
									isReturnFlight={props.isReturnFlight}
								/>
							))}

							{props.flight.segments.length > 1 && (
								<div className={theme.total_time}>
									{t('Total duration')}: {getDurationString(getFlightDuration(props.flight.segments))}
								</div>
							)}

							<div className={theme.content_footer}>
								<BaseButton variant="smallPrimary" onClick={onScrollTop} className={theme.scroll_up}>
									{t('Scroll up')}
								</BaseButton>
							</div>
						</div>
					)}
				</div>
			</div>

			<ModalX
				isOpen={isFareCompareOpen}
				noPadding={true}
				size="xlarge"
				onClose={closeFareCompare}
				scroll="body"
				keepMounted={false}
			>
				<CompareFares orderId={props.orderId} />
			</ModalX>
		</Paper>
	);
};

export default DesktopFlightModalContent;
